import React, { useState } from 'react';
import { Avatar, Paper, Grid, Typography, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { LocalActivity } from '@material-ui/icons';
import { orange } from '@material-ui/core/colors';
import moment from 'moment';
import AuditTrail from '../AuditTrail';
import EventTour from '../EventTour';
import KillshotNotifications from '../KillshotNotifications';
import { getLogUserOrg, hasAutomationTabAccess } from '../../helpers/user';
import NotificationSnackBar from '../Common/Snackbar';
import AutomationChip from './AutomationChip';

const NOTIFICATION_RING_BELL_HIDDEN = [43];
const WIDTH = 53;
const INIT_TOP = 85;

const useStyles = makeStyles({
  root: {
    marginTop: 100,
  },
  topScrollPaper: {
    alignItems: 'flex-start',
    justifyContent: 'end',
  },
  topPaperScrollBody: {
    verticalAlign: 'top',
  },
  auditHistory: {
    display: 'flex',
    justifySelf: 'flex-end',
    alignItems: 'center',
    padding: '5px',
  },
  auditContent: {
    maxHeight: '600px',
    maxWidth: '600px',
    width: '100%',
    height: '100%',
  },
  newNotification: {
    color: orange[500],
    marginTop: '-10px',
  },
  metaHeaderGrid: {
    '& .MuiGrid-item:last-child': {
      marginLeft: 'auto',
    },
  },
  headerOptions: {
    alignItems: 'center',
    padding: '0px',
    gap: '12px',
  },
  headerButtons: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
  },
  headerIconButtons: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
  },
  horizontalLine: {
    border: '1px solid #9C27B0',
    color: '#9C27B0',
  },
  popUpTextStyle: {
    fontWeight: 600,
    fontSize: '14px',
    color: '#262626',
    lineHeight: 'bold',
  },
  clickableItem: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
});

const MetaHeaderWrap = styled(Paper)`
  padding: 10px;
  position: sticky;
  top: 0;
  z-index: 2;
  margin-bottom: 10px;
`;

const MetaHeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
`;

const MetaHeaderSectionFirst = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
`;

const OnsaleMetaHeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  white-space: nowrap;
`;

const DateIfAvailable = props => {
  const { label, date, timezone, localDate } = props;
  const format = 'MM/DD/YYYY hh:mm a ddd';

  if (!moment(date).isValid()) {
    return (
      <Typography>
        {label && `${label}: `}
        Unavailable
      </Typography>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {label && <Typography>{`${label}: `}</Typography>}
      <div>
        <Typography variant="body2">
          {timezone
            ? moment
                .utc(date)
                .tz(timezone)
                .format(format)
            : moment.utc(date).format(format)}
        </Typography>
        {timezone && <Typography variant="body2">{`${moment(localDate || date).format(format)} PT`}</Typography>}
      </div>
    </div>
  );
};

DateIfAvailable.propTypes = {
  label: PropTypes.string,
  date: PropTypes.string,
  timezone: PropTypes.string,
  localDate: PropTypes.string,
};

DateIfAvailable.defaultProps = {
  label: null,
  date: null,
  timezone: '',
  localDate: '',
};

const MetaHeader = props => {
  const { eventMeta } = props;
  const userOrgId = getLogUserOrg();
  const classes = useStyles();
  const timezone = eventMeta.venue ? eventMeta.venue.timezone : null;
  const [newData, setNewData] = useState([]);
  const [earlierData, setEarlierData] = useState([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [lastJsonMessageParent, setLastJsonMessageParent] = useState([]);
  const showAutomationChip = hasAutomationTabAccess();

  const handlePopupClose = () => {
    setPopupOpen(false);
  };

  const handlePopupOnclick = () => {
    setPopupOpen(false);
    setNotificationOpen(true);
  };

  return (
    <>
      <MetaHeaderWrap square>
        <Grid container spacing={2} className={classes.metaHeaderGrid}>
          <Grid item>
            <MetaHeaderSectionFirst>
              <Avatar aria-label="Event Stats" style={{ background: '#7D1F8D', marginRight: '5px' }}>
                <LocalActivity style={{ color: 'white' }} />
              </Avatar>
              <Typography inline>
                <strong>{eventMeta.name}</strong>
              </Typography>
            </MetaHeaderSectionFirst>
          </Grid>
          <Grid item>
            <MetaHeaderSection>
              <Typography>{typeof eventMeta.venue === 'string' ? eventMeta.venue : eventMeta?.venue?.name}</Typography>
            </MetaHeaderSection>
          </Grid>
          <Grid item>
            <MetaHeaderSection>
              <DateIfAvailable date={eventMeta.event_date} localDate={eventMeta.event_date_local} timezone={timezone} />
            </MetaHeaderSection>
          </Grid>
          <Grid item>
            <MetaHeaderSection>
              <Typography>
                days until event:&nbsp;
                {eventMeta.days_until_event}
              </Typography>
            </MetaHeaderSection>
          </Grid>
          <Grid item>
            <OnsaleMetaHeaderSection>
              <DateIfAvailable
                label="onsale date"
                date={eventMeta.on_sale}
                localDate={eventMeta.on_sale_local}
                timezone={timezone}
              />
            </OnsaleMetaHeaderSection>
          </Grid>
          <Grid item>
            <MetaHeaderSection>
              <Typography>{eventMeta.genre && eventMeta.genre.genre}</Typography>
            </MetaHeaderSection>
          </Grid>
          <Grid item>
            <MetaHeaderSection>
              <span>{eventMeta.event_links}</span>
            </MetaHeaderSection>
          </Grid>
          <Grid item className={classes.auditHistory}>
            <Grid container className={classes.headerOptions}>
              <Grid item className={classes.headerButtons}>
                {showAutomationChip ? <AutomationChip lobsangEventId={eventMeta.event_id} /> : null}
                <AuditTrail eventMeta={eventMeta} />
              </Grid>

              <Grid item className={classes.headerIconButtons}>
                <EventTour eventMeta={eventMeta} />
                {!NOTIFICATION_RING_BELL_HIDDEN.includes(userOrgId) && (
                  <KillshotNotifications
                    earlierData={earlierData}
                    newData={newData}
                    notificationOpen={notificationOpen}
                    setLastJsonMessageParent={setLastJsonMessageParent}
                    setEarlierData={setEarlierData}
                    setNewData={setNewData}
                    setNotificationOpen={setNotificationOpen}
                    setPopupOpen={setPopupOpen}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MetaHeaderWrap>
      {lastJsonMessageParent &&
        lastJsonMessageParent.message &&
        Array.isArray(lastJsonMessageParent.message) &&
        lastJsonMessageParent.message.length > 0 &&
        lastJsonMessageParent.message.map((message, i) => (
          <NotificationSnackBar
            lastJsonMessage={message}
            handlePopupClose={handlePopupClose}
            handlePopupOnclick={handlePopupOnclick}
            popupOpen={popupOpen}
            width={`${WIDTH}px`}
            top={`${i * WIDTH + (i > 0 ? 10 : 0) + INIT_TOP}px`}
          />
        ))}
    </>
  );
};

MetaHeader.propTypes = {
  eventMeta: PropTypes.object.isRequired,
};

export default MetaHeader;
